import type { ReactiveController, ReactiveControllerHost } from 'lit';
import type { MorningCheckResponse } from './morning-check-api.js';
import * as morningCheckApi from './morning-check-api.js';
import { wsClient } from './websocket-client.js';

export type MorningCheckLoadingState = 'idle' | 'loading' | 'loaded' | 'error';
export type TriggerState = 'idle' | 'triggering';

let sharedData: MorningCheckResponse | null = null;
let sharedLoadingState: MorningCheckLoadingState = 'idle';
let sharedError: string | null = null;
let sharedTriggerState: TriggerState = 'idle';

const hosts = new Set<ReactiveControllerHost>();

function notifyHosts(): void {
  for (const host of hosts) {
    host.requestUpdate();
  }
}

async function loadMorningCheck(): Promise<void> {
  if (sharedLoadingState === 'loading') return;
  sharedLoadingState = 'loading';
  sharedError = null;
  notifyHosts();

  try {
    sharedData = await morningCheckApi.getMorningCheck();
    sharedLoadingState = 'loaded';
  } catch (e) {
    sharedLoadingState = 'error';
    if (e instanceof Error && e.message.includes('404')) {
      sharedError = null;
      sharedLoadingState = 'loaded';
      sharedData = null;
    } else {
      sharedError = e instanceof Error ? e.message : 'Morning Check konnte nicht geladen werden';
    }
  }

  notifyHosts();
}

function onWsMessage(e: Event): void {
  const detail = (e as CustomEvent).detail as { type: string };

  if (detail.type === 'morning-check:completed') {
    void loadMorningCheck();
  }
}

wsClient.addEventListener('ws-message', onWsMessage);

export class MorningCheckStateController implements ReactiveController {
  private host: ReactiveControllerHost;

  constructor(host: ReactiveControllerHost) {
    this.host = host;
    host.addController(this);
  }

  hostConnected(): void {
    hosts.add(this.host);
  }

  hostDisconnected(): void {
    hosts.delete(this.host);
  }

  get data(): MorningCheckResponse | null {
    return sharedData;
  }

  get loadingState(): MorningCheckLoadingState {
    return sharedLoadingState;
  }

  get error(): string | null {
    return sharedError;
  }

  get triggerState(): TriggerState {
    return sharedTriggerState;
  }

  get hasData(): boolean {
    return sharedData !== null;
  }

  async load(): Promise<void> {
    await loadMorningCheck();
  }

  async triggerDailyCheck(): Promise<void> {
    if (sharedTriggerState === 'triggering') return;
    sharedTriggerState = 'triggering';
    notifyHosts();

    try {
      await morningCheckApi.triggerDailyCheck();
      // Result comes via WebSocket 'morning-check:completed'
    } catch (e) {
      sharedError = e instanceof Error ? e.message : 'Morning Check konnte nicht gestartet werden';
    }

    sharedTriggerState = 'idle';
    notifyHosts();
  }
}
